import React, { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import { urlifyName } from "../shared/Utils/PageUrl";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link, useNavigate } from "react-router-dom";
import { getAnnouncements } from "../../api/Announcements";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {
  getPaginatedCampaigns,
  getAllPaginatedCampaigns,
} from "../../api/Campaigns";
import UsaMap from "./UsaMap/UsaMap";
import classes from "./Homepage.module.css";
import CampaignCard from "../Campaigns/CampaignCard/CampaignCard";
import FeaturedCampaignCard from "./FeaturedCampaignCard";
import { subscribeMarketingNewsletter } from "../../api/SubscribeNewsletter";
import { errorHandler } from "../shared/Utils/ErrorHandler";
import Spinner from "../shared/LoadingSpinner/Spinner";
import { useMediaQuery } from "@mui/material";
import Diagram from "./Diagram";
import {
  faArrowRight,
  faPiggyBank,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import VerifiedIcon from "@mui/icons-material/Verified";
import PrimaryButton from "../shared/Buttons/PrimaryButton";
import BlueButton from "../shared/Buttons/BlueButton";
import {
  MoneyWavy,
  PawPrint,
  // ShoppingCart, // Commented out - shop feature disabled
  Handshake,
  Megaphone,
  HandHeart,
  ShieldCheck,
  House,
  CaretLeft,
  CaretRight,
  CaretCircleLeft,
  CaretCircleRight,
  HouseLine,
  Download,
  PresentationChart,
  ClipboardText,
} from "@phosphor-icons/react";
import CircleFeature from "./CircleFeature";
import ServiceCard from "./ServiceCard";

// Images
const desktopBackground =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/newsletter-bg.png";
const mobileBackground =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/newsletter-bg-mobile.png";
const furfeather =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/furfeather.png";
const logo4dogs =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/4dogslogo.png";
const quoteIcon =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/Frame+1000006781.png";

const bannerMobile =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/dogCatMobile.png";
const bannerCatDog =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/catDog.png";
const NPofTheMonth =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/headingDesktop.png";
const NPofTheMonthMobile =
  "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_images/np_of_the_month_mobile.png";

// Creating logo list
function shuffle(array) {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex > 0) {
    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}
const logos = require.context("../../assets/nonprofitLogos", true);
const logoList = shuffle(logos.keys().map((logo) => logos(logo)));

const testimonials = [
  {
    content:
      "A refreshing plaform that is based on integrity, putting nonprofits first.",
    img: furfeather,
    name: "Nancy Sayle",
    nonprofit: "Fur & Feather",
  },
  {
    content: "They truly are the Happy Platform. People seek them out.",
    img: logo4dogs,
    name: "Dennise Totes",
    nonprofit: "4 Dogs Farm Rescue",
  },
];

// Services data
const services = [
  {
    id: 1,
    title: "Marketing & Social Media",
    description:
      "Building your nonprofit's visibility and community connection through strategic outreach.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/marketing.png",
    link: "/consulting-services/marketing",
  },
  {
    id: 2,
    title: "Fundraising",
    description:
      "Streamlining your operations and enhancing collaboration with strategic partners.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/fundraising.png",
    link: "/consulting-services/fundraising",
  },
  {
    id: 3,
    title: "Website Technology",
    description:
      "Implementing robust technology solutions to manage and leverage your organization's data.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/doglaptop.png",
    link: "/consulting-services/technology",
  },
  {
    id: 4,
    title: "Google Adwords Grant Management",
    description:
      "Developing sustainable funding strategies and donor relationship management.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/googleadwords.png",
    link: "/consulting-services/google-adwords",
  },
  {
    id: 5,
    title: "Operations & Partner Support",
    description:
      "Strategic planning and execution of fundraising campaigns to maximize donor engagement and contributions.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/operations.png",
    link: "/consulting-services",
  },
  {
    id: 6,
    title: "Corporate Sponsorships",
    description:
      "Enhancing your online presence and community engagement through targeted digital strategies.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/corporatesponsorship.png",
    link: "/consulting-services/corporate-sponsorship",
  },
  {
    id: 7,
    title: "Search Engine Optimization (SEO)",
    description:
      "Identifying funding opportunities and developing compelling proposals to secure financial support.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/seo.png",
    link: "/consulting-services/seo",
  },
  {
    id: 8,
    title: "Grant Writing",
    description:
      "Creating structured programs to recruit, train, and retain dedicated volunteers.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/catchecklist.png",
    link: "/consulting-services/grant-writing",
  },
  {
    id: 9,
    title: "Donor Discovery",
    description:
      "Building mutually beneficial relationships with businesses to increase resources and impact.",
    image:
      "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/donordiscovery.png",
    link: "/consulting-services/donor-discovery",
  },
];

// Add video URLs constant
const VIDEOS = {
  donate:
    "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_videos/SAP_Donate_Vid_13.mp4",
  petstar:
    "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_videos/Welcome+to+The+PetStar+Club2.mp4",
  // products: // Commented out - shop feature disabled
  //   "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_videos/Big+news+is+just+around+the+corner....mp4",
  consulting:
    "https://sap-app-prod.s3.us-west-2.amazonaws.com/homepage_videos/Consulting+Services+Short.mp4",
};

// Add a more sophisticated resource loading manager at the top of the file
const ResourcePriority = {
  CRITICAL: "critical", // First video
  HIGH: "high", // Next videos
  MEDIUM: "medium", // Top components
  LOW: "low", // Everything else
  DEFER: "defer", // Load last
};

// Enhanced optimizeImageLoading function with priority levels
const optimizeImageLoading = (src, priority = ResourcePriority.LOW) => {
  if (priority === ResourcePriority.CRITICAL) {
    return {
      src,
      fetchpriority: "high",
      loading: "eager",
    };
  }

  if (priority === ResourcePriority.HIGH) {
    return {
      src,
      fetchpriority: "high",
      loading: "eager",
    };
  }

  if (priority === ResourcePriority.MEDIUM) {
    return {
      src,
      loading: "eager",
    };
  }

  if (priority === ResourcePriority.LOW) {
    return {
      src,
      loading: "lazy",
    };
  }

  // DEFER - load absolutely last
  return {
    "data-src": src,
    src: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E", // Tiny placeholder
    loading: "lazy",
  };
};

// Improved VideoPreloader to handle the sequence of loads
function VideoPreloader() {
  useEffect(() => {
    // Create a loading sequence manager
    const loadManager = {
      phase: 0, // 0: first video, 1: other videos, 2: top components, 3: rest
      videosDone: false,
      topComponentsDone: false,

      // Move to next phase
      nextPhase() {
        this.phase++;
        this.executePhase();
      },

      // Execute current phase
      executePhase() {
        switch (this.phase) {
          case 0: // First video - already handled in main component
            break;
          case 1: // Other videos
            this.loadOtherVideos();
            break;
          case 2: // Top components
            this.loadTopComponents();
            break;
          case 3: // Everything else
            this.loadEverythingElse();
            break;
        }
      },

      // Load other videos
      loadOtherVideos() {
        const otherVideos = ["petstar", "consulting"];

        // Create preload links with medium priority
        otherVideos.forEach((key) => {
          const link = document.createElement("link");
          link.rel = "preload";
          link.href = VIDEOS[key];
          link.as = "video";
          link.type = "video/mp4";
          link.importance = "high";
          document.head.appendChild(link);
        });

        this.videosDone = true;
        this.nextPhase();
      },

      // Load top components
      loadTopComponents() {
        // Identify and preload critical top components
        const topComponentImages = [
          // Banner images
          "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/pawbanner2.png",
          "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/pawbannermobile.png",

          // Header repeating pattern/banner
          "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/dogandcat.png",

          // Top section images
          "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/flowercat.png",
          "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/picnic.png",
        ];

        // Preload these images
        topComponentImages.forEach((src) => {
          const link = document.createElement("link");
          link.rel = "preload";
          link.href = src;
          link.as = "image";
          link.importance = "high";
          document.head.appendChild(link);
        });

        // Enable loading for top component images
        document
          .querySelectorAll('img[data-priority="medium"]')
          .forEach((img) => {
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute("data-src");
            }
          });

        this.topComponentsDone = true;
        this.nextPhase();
      },

      // Load everything else
      loadEverythingElse() {
        // Enable loading for all remaining images
        document.querySelectorAll("img[data-src]").forEach((img) => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute("data-src");
          }
        });
      },
    };

    // Add preconnect for S3 domain
    const connectPreload = document.createElement("link");
    connectPreload.rel = "preconnect";
    connectPreload.href = "https://sap-app-prod.s3.us-west-2.amazonaws.com";
    connectPreload.crossOrigin = "anonymous";
    document.head.appendChild(connectPreload);

    // DNS prefetch as well
    const dnsPrefetch = document.createElement("link");
    dnsPrefetch.rel = "dns-prefetch";
    dnsPrefetch.href = "https://sap-app-prod.s3.us-west-2.amazonaws.com";
    document.head.appendChild(dnsPrefetch);

    // Create preload link for primary video with high priority
    const firstVideoLink = document.createElement("link");
    firstVideoLink.rel = "preload";
    firstVideoLink.href = VIDEOS.donate;
    firstVideoLink.as = "video";
    firstVideoLink.type = "video/mp4";
    firstVideoLink.importance = "high";
    firstVideoLink.setAttribute("fetchpriority", "high");
    document.head.appendChild(firstVideoLink);

    // Listen for first video load completion
    const checkFirstVideoLoaded = () => {
      // If first video is loaded, move to next phase
      if (window.sapFirstVideoLoaded) {
        loadManager.nextPhase();
        clearInterval(checkInterval);
      }
    };

    // Check every 100ms if first video is loaded
    const checkInterval = setInterval(checkFirstVideoLoaded, 100);

    // If video doesn't load within 5s, proceed anyway
    const timeout = setTimeout(() => {
      if (!window.sapFirstVideoLoaded) {
        window.sapFirstVideoLoaded = true;
        clearInterval(checkInterval);
      }
    }, 5000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
      document.head.removeChild(firstVideoLink);
      document.head.removeChild(connectPreload);
      document.head.removeChild(dnsPrefetch);
    };
  }, []);

  return null;
}

function HomePage() {
  const [announcements, setAnnouncements] = useState([]);
  const [campaignsSubset, setCampaignsSubset] = useState([]);
  const [email, setEmail] = useState("");
  const { width } = useWindowDimensions();
  const sliderRef = useRef(null);
  const featuredCarouselRef = useRef(null);
  const [hover, setHover] = useState(-1);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [featuredCarouselPosition, setFeaturedCarouselPosition] = useState(0);
  const [isFeaturedCarouselPaused, setIsFeaturedCarouselPaused] =
    useState(false);
  const [animalSearch, setAnimalSearch] = useState({
    search: "",
    species: "",
    age: -1,
    state: "",
    nonprofitId: "",
  });
  const servicesCarouselRef = useRef(null);
  const [servicesCarouselPosition, setServicesCarouselPosition] = useState(0);
  const [isServicesCarouselPaused, setIsServicesCarouselPaused] =
    useState(false);
  const [currentVideo, setCurrentVideo] = useState(VIDEOS.donate);
  const [activeVideoKey, setActiveVideoKey] = useState("donate");
  const [videoProgress, setVideoProgress] = useState(0);
  const [buttonProgress, setButtonProgress] = useState({
    donate: 0,
    petstar: 0,
    // products: 0, // Commented out - shop feature disabled
    consulting: 0,
  });
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const preloadedVideosRef = useRef({});
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Create a persistent reference for the canPlay event handler outside the effect
  const handleFirstVideoCanPlayRef = useRef(null);

  // Prioritize video loading by deferring image loading
  useEffect(() => {
    // Create a function to enable image loading
    const enableImageLoading = () => {
      // Find all images with loading="lazy" or data-src attributes
      const images = document.querySelectorAll(
        'img[loading="lazy"], img[data-src]',
      );

      // Enable loading for those images
      images.forEach((img) => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute("data-src");
        }
        img.loading = "lazy";
      });

      setImagesLoaded(true);
    };

    // Connect this to video loading completion
    if (preloadedVideosRef.current.donate === true) {
      // First video is loaded, now we can load images
      enableImageLoading();
    }
  }, [preloadedVideosRef.current.donate]);

  // Optimized video loading: ONLY load the first video initially, then load others on demand
  useEffect(() => {
    // First video key (the one we want to load first)
    const firstVideoKey = "donate";

    // Set initial loading state
    setIsVideoLoading(true);

    // Initialize tracking for loaded videos
    preloadedVideosRef.current = { [firstVideoKey]: false };

    // Create the handler function and store in the persistent ref
    handleFirstVideoCanPlayRef.current = () => {
      preloadedVideosRef.current[firstVideoKey] = true;
      setIsVideoLoading(false);

      // Set global flag to indicate first video loaded
      window.sapFirstVideoLoaded = true;

      try {
        // Ensure video is muted for autoplay (with safety checks)
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current
            .play()
            .then(() => {
              // Preload the next video in sequence
              const videoOrder = [
                "donate",
                "petstar",
                "products",
                "consulting",
              ];
              const nextVideoKey = videoOrder[1]; // "petstar" is next after "donate"
              preloadNextVideo(nextVideoKey);
            })
            .catch((err) =>
              console.error("Could not autoplay first video:", err),
            );
        }
      } catch (error) {
        console.error("Error playing first video:", error);
      }
    };

    // Prioritize video loading with resource hints
    if (typeof window !== "undefined") {
      // Tell browser to deprioritize images until video is loaded
      const imageRequests = window.performance
        ?.getEntriesByType?.("resource")
        .filter((r) => r.initiatorType === "img" && !r.responseEnd);

      if (imageRequests?.length && navigator.connection?.saveData === false) {
        // If browser supports it and user is not in data saver mode
        // We can use fetch() with { priority: 'low' } for images
        // This is just a hint to the browser - not actually fetching
        imageRequests.forEach((r) => {
          try {
            if ("priority" in Request.prototype) {
              fetch(r.name, {
                priority: "low",
                cache: "force-cache",
                mode: "no-cors",
                keepalive: false,
              }).catch(() => {});
            }
          } catch (e) {}
        });
      }
    }

    // Load only the first video initially
    if (videoRef.current) {
      // Set source for the first video
      videoRef.current.src = VIDEOS[firstVideoKey];

      // Explicitly tell the browser to preload this first video completely
      videoRef.current.preload = "auto";

      // Set the highest fetch priority
      videoRef.current.setAttribute("fetchpriority", "high");

      // Set priority hint if supported
      if ("priority" in videoRef.current) {
        videoRef.current.priority = "high";
      }

      // Add the event listener using the reference
      videoRef.current.addEventListener(
        "canplay",
        handleFirstVideoCanPlayRef.current,
      );

      // Handle loading errors
      videoRef.current.addEventListener("error", () => {
        console.error(`Error loading first video (${firstVideoKey})`);
        setIsVideoLoading(false);
      });

      // Initiate loading
      videoRef.current.load();
    }

    // No preloading of other videos - we'll load them on demand when needed
    return () => {
      // Clean up any event listeners if component unmounts
      if (videoRef.current) {
        // Use the reference to remove the correct handler
        if (handleFirstVideoCanPlayRef.current) {
          videoRef.current.removeEventListener(
            "canplay",
            handleFirstVideoCanPlayRef.current,
          );
        }
        videoRef.current.removeEventListener("error", () => {});

        // Stop video loading/playing on unmount
        videoRef.current.pause();
        videoRef.current.src = "";
      }
    };
  }, []);

  // Add a function to preload the next video
  const preloadNextVideo = (videoKey) => {
    if (!videoKey || preloadedVideosRef.current[videoKey]) return;

    // Create a new video element for preloading
    const preloadVideo = document.createElement("video");
    preloadVideo.preload = "auto";
    preloadVideo.muted = true;
    preloadVideo.src = VIDEOS[videoKey];

    // Set attributes for high priority loading
    preloadVideo.setAttribute("fetchpriority", "high");

    // Track when preloading is complete
    preloadVideo.addEventListener("canplaythrough", () => {
      preloadedVideosRef.current[videoKey] = true;

      // Clean up the element after preloading
      preloadVideo.src = "";
    });

    // Handle errors
    preloadVideo.addEventListener("error", () => {
      console.error(`Error preloading video: ${videoKey}`);
    });

    // Start loading
    preloadVideo.load();
  };

  const matches840px = useMediaQuery("(max-width: 840px)");

  // Function to handle video cycling with loading indicator
  const handleVideoEnd = () => {
    const videoOrder = ["donate", "petstar", "consulting"];
    const currentIndex = videoOrder.indexOf(activeVideoKey);
    const nextIndex = (currentIndex + 1) % videoOrder.length;
    const nextKey = videoOrder[nextIndex];

    // Preload the video after the next one in sequence (looking ahead)
    const preloadIndex = (nextIndex + 1) % videoOrder.length;
    const preloadKey = videoOrder[preloadIndex];

    // Reset all progress values
    setButtonProgress({
      donate: 0,
      petstar: 0,
      // products: 0, // Commented out - shop feature disabled
      consulting: 0,
    });

    // If next video isn't already preloaded, show loading indicator
    setIsVideoLoading(!preloadedVideosRef.current[nextKey]);

    // Set states for the next video
    setCurrentVideo(VIDEOS[nextKey]);
    setActiveVideoKey(nextKey);
    setVideoProgress(0);

    // Load the next video - it might already be preloaded
    if (videoRef.current) {
      videoRef.current.src = VIDEOS[nextKey];
      videoRef.current.preload = "auto";
      videoRef.current.load();

      // Try to play immediately if already preloaded
      if (preloadedVideosRef.current[nextKey]) {
        videoRef.current
          .play()
          .then(() => {
            // Preload the next one in sequence while this one plays
            preloadNextVideo(preloadKey);
          })
          .catch((err) =>
            console.error(`Error playing preloaded video ${nextKey}:`, err),
          );
      }
    }
  };

  // Function to handle video time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress =
        (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);

      // Update only the active button's progress
      setButtonProgress((prev) => ({
        ...prev,
        [activeVideoKey]: progress,
      }));

      // When the video reaches 50%, preload the next video in sequence
      if (progress > 50 && progress < 55) {
        const videoOrder = ["donate", "petstar", "consulting"];
        const currentIndex = videoOrder.indexOf(activeVideoKey);
        const nextIndex = (currentIndex + 1) % videoOrder.length;
        const nextKey = videoOrder[nextIndex];

        // Only preload if not already loaded
        if (!preloadedVideosRef.current[nextKey]) {
          preloadNextVideo(nextKey);
        }
      }
    }
  };

  // Function to change video with optimized loading behavior
  const changeVideo = (videoKey) => {
    // If trying to load the same video, ignore
    if (videoKey === activeVideoKey) return;

    // Reset all progress values
    setButtonProgress({
      donate: 0,
      petstar: 0,
      // products: 0, // Commented out - shop feature disabled
      consulting: 0,
    });

    // Update states
    setVideoProgress(0);
    setActiveVideoKey(videoKey);
    setCurrentVideo(VIDEOS[videoKey]);

    // Only show loading if not already preloaded
    setIsVideoLoading(!preloadedVideosRef.current[videoKey]);

    // Load the requested video on demand
    if (videoRef.current) {
      // Set the video source
      videoRef.current.src = VIDEOS[videoKey];

      // High priority for subsequent videos but still efficient loading
      videoRef.current.preload = "auto";
      videoRef.current.setAttribute("fetchpriority", "high");
      videoRef.current.setAttribute("importance", "high");
      videoRef.current.setAttribute("data-priority", "high");

      // Reset time and start loading
      videoRef.current.currentTime = 0;
      videoRef.current.load();

      // Set up one-time event listener for when video can play
      const handleCanPlay = () => {
        preloadedVideosRef.current[videoKey] = true;
        setIsVideoLoading(false);

        // Play the video
        videoRef.current
          .play()
          .then(() => {
            // Preload the next video in sequence
            const videoOrder = ["donate", "petstar", "consulting"];
            const currentIndex = videoOrder.indexOf(videoKey);
            const nextIndex = (currentIndex + 1) % videoOrder.length;
            const nextKey = videoOrder[nextIndex];

            // Only preload if not already loaded
            if (!preloadedVideosRef.current[nextKey]) {
              preloadNextVideo(nextKey);
            }
          })
          .catch((err) =>
            console.error(`Error playing ${videoKey} video:`, err),
          );

        // Remove this listener since it's one-time use
        videoRef.current.removeEventListener("canplay", handleCanPlay);
      };

      // Add the event listener
      videoRef.current.addEventListener("canplay", handleCanPlay);
    }
  };

  // Add unmute on first interaction with proper null checking
  useEffect(() => {
    const handleUserInteraction = () => {
      try {
        if (videoRef.current && isMuted) {
          // Safely set muted property with null checking
          videoRef.current.muted = false;
          setIsMuted(false);

          // Remove event listeners after successful unmuting
          document.removeEventListener("click", handleUserInteraction);
          document.removeEventListener("touchstart", handleUserInteraction);
        }
      } catch (error) {
        console.error("Error while trying to unmute video:", error);
      }
    };

    // Short delay to ensure video element is ready
    const timer = setTimeout(() => {
      document.addEventListener("click", handleUserInteraction);
      document.addEventListener("touchstart", handleUserInteraction);
    }, 500);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, [isMuted]);

  // Getting api responses
  useEffect(() => {
    const fetchAllData = () => {
      setIsLoading(true);

      getAnnouncements()
        .then((response) => {
          setAnnouncements(response.slice(0, 3));
        })
        .catch((error) => {
          console.error("Could not retrieve announcements", error);
        });

      getAllPaginatedCampaigns(1)
        .then((response) => {
          // Set campaigns directly from the response without duplication
          if (response.results && response.results.length > 0) {
            setCampaignsSubset(response.results.slice(0, 8));
          } else {
            setCampaignsSubset([]);
          }
        })
        .catch((error) => {
          console.error("Could not retrieve campaigns", error);
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 300);
        });
    };

    fetchAllData();
  }, []);

  // Determine number of visible cards based on screen width
  const getVisibleCardCount = () => {
    if (width < 655) return 1; // Mobile: 1 card
    if (width < 960) return 2; // Small tablets: 2 cards
    if (width < 1260) return 3; // Large tablets: 3 cards
    return 4; // Desktop: 4 cards
  };

  // Calculate container width based on visible cards
  const getContainerWidth = () => {
    const cardWidth = 278; // Width of each card
    const gap = 40; // Gap between cards (gap-10)
    const visibleCards = getVisibleCardCount();

    // Calculate: (cards × cardWidth) + (gaps × gapWidth)
    return visibleCards * cardWidth + (visibleCards - 1) * gap;
  };

  // Handle carousel scroll with responsive card count
  const scrollCarousel = (direction) => {
    if (featuredCarouselRef.current) {
      const cardWidth = 278; // Width of each card
      const gap = 40; // Gap between cards (gap-10)
      const scrollAmount = cardWidth + gap; // Scroll amount for one card

      // Get the current visible card count
      const visibleCards = getVisibleCardCount();

      // Total number of cards that can be scrolled (total cards - visible cards)
      const maxPosition = Math.max(0, campaignsSubset.length - visibleCards);

      // Update position based on direction
      let newPosition;
      if (direction === "left") {
        newPosition = Math.max(0, featuredCarouselPosition - 1);
      } else {
        newPosition = Math.min(maxPosition, featuredCarouselPosition + 1);
      }

      // Set the new position
      setFeaturedCarouselPosition(newPosition);

      // Scroll to the new position
      featuredCarouselRef.current.scrollTo({
        left: newPosition * scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Reset carousel position when screen size changes
  useEffect(() => {
    // Reset position to 0 when screen width changes
    setFeaturedCarouselPosition(0);

    // Scroll back to start
    if (featuredCarouselRef.current) {
      featuredCarouselRef.current.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  }, [width]);

  // Reset services carousel position when screen size changes
  useEffect(() => {
    // Reset position to 0 when screen width changes
    setServicesCarouselPosition(0);

    // Scroll back to start
    if (servicesCarouselRef.current) {
      servicesCarouselRef.current.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  }, [width]);

  // Auto-scroll carousel every 5 seconds
  useEffect(() => {
    // Skip auto-scroll if paused
    if (isFeaturedCarouselPaused) return;

    const interval = setInterval(() => {
      if (featuredCarouselRef.current) {
        // Get visible card count based on screen width
        let visibleCards = 4;
        if (width < 700) visibleCards = 1;
        else if (width < 970) visibleCards = 2;
        else if (width < 1380) visibleCards = 3;

        // Calculate max position
        const maxPosition = Math.max(0, campaignsSubset.length - visibleCards);

        // Calculate next position (with looping)
        let nextPosition = featuredCarouselPosition + 1;
        if (nextPosition > maxPosition) {
          nextPosition = 0; // Loop back to start
        }

        // Update position state
        setFeaturedCarouselPosition(nextPosition);

        // Calculate scroll amount
        const cardWidth = 278;
        const gap = 40;
        const scrollAmount = cardWidth + gap;

        // Scroll to next position
        featuredCarouselRef.current.scrollTo({
          left: nextPosition * scrollAmount,
          behavior: "smooth",
        });
      }
    }, 5000); // 5 seconds interval

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [
    featuredCarouselPosition,
    campaignsSubset.length,
    width,
    isFeaturedCarouselPaused,
  ]);

  function handleGetStarted(data) {
    if (data) {
      subscribeMarketingNewsletter({ email: data, active: false })
        .then(() => {
          setEmail("");
          alert("Thank you for subscribing to our newsletter!");
        })
        .catch((error) => {
          console.error(error);
          const err = errorHandler(error);
          alert("Error while subscribing to our newsletter: " + err);
        });
    } else {
      alert("Please enter an email address.");
    }
  }

  let slideshowSettings = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    speed: 1000,
    autoplaySpeed: 10000, // 10 secs
    pauseOnHover: false,
  };

  let logoCarouselSettings = {
    dots: false,
    infinite: true,
    slidesToShow: 8,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    speed: 4000,
    autoplaySpeed: 0,
    pauseOnHover: false,
    cssEase: "linear",
  };

  let logoCarouselSettingsMedium = {
    ...logoCarouselSettings,
    slidesToShow: 4,
  };
  let logoCarouselSettingsSmall = {
    ...logoCarouselSettings,
    slidesToShow: 3,
    speed: 2000,
  };

  const handleMouseEnter = (id) => {
    if (sliderRef.current) {
      sliderRef.current.slickPause();
    }
    setHover(id);
  };

  const handleMouseLeave = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPlay();
    }
    setHover(-1);
  };

  const handleClick = (campaign) => {
    window.scrollTo(0, 0);
    const campaignPath =
      campaign.campaign_type === "animal"
        ? `/campaigns/animal/${urlifyName(
            campaign.nonprofit_name,
          )}/${urlifyName(campaign.animal_name)}/${campaign.id}`
        : `/campaigns/${campaign.campaign_type}/${urlifyName(
            campaign.nonprofit_name,
          )}/${urlifyName(campaign.campaign_title)}/${campaign.id}`;

    navigate(campaignPath);
  };

  // Function to get image props based on current state and priority
  const getImageProps = (src, priority = ResourcePriority.LOW) => {
    // Critical images load immediately regardless of video state
    if (priority === ResourcePriority.CRITICAL) {
      return optimizeImageLoading(src, ResourcePriority.CRITICAL);
    }

    // For HIGH priority, load if first video has started loading
    if (
      priority === ResourcePriority.HIGH &&
      preloadedVideosRef.current.donate === true
    ) {
      return optimizeImageLoading(src, ResourcePriority.HIGH);
    }

    // For MEDIUM priority, load if first video loaded
    if (priority === ResourcePriority.MEDIUM && window.sapFirstVideoLoaded) {
      return optimizeImageLoading(src, ResourcePriority.MEDIUM);
    }

    // For everything else, defer loading
    return optimizeImageLoading(src, ResourcePriority.DEFER);
  };

  if (isLoading) {
    return (
      <div className="flex flex-row ml-auto mt-[35vh] w-full min-h-[20rem] justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bg-white w-full min-h-[100rem] flex flex-col overflow-x-hidden">
      {/* Include the VideoPreloader component */}
      <VideoPreloader />
      {/* Top sections with background image */}
      <div
        className="w-full mt-24 relative overflow-x-hidden"
        style={{
          backgroundImage: matches840px
            ? `url(${
                getImageProps(
                  "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/pawbannermobile.png",
                  ResourcePriority.MEDIUM,
                ).src
              })`
            : `url(${
                getImageProps(
                  "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/pawbanner2.png",
                  ResourcePriority.MEDIUM,
                ).src
              })`,
          backgroundSize: "contain",
          backgroundPosition: "center top",
          backgroundRepeat: "repeat-y repeat-x",
          backgroundAttachment: "scroll",
        }}
      >
        {/* If video not loaded yet, use a simple color background */}
        {!preloadedVideosRef.current.donate && (
          <div className="absolute inset-0 bg-white"></div>
        )}

        {/* Impact Hero Section */}
        <div
          className="w-full mt-24 relative overflow-x-hidden"
          style={{
            backgroundImage: matches840px
              ? `url(${
                  getImageProps(
                    "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/pawbannermobile.png",
                    ResourcePriority.MEDIUM,
                  ).src
                })`
              : `url(${
                  getImageProps(
                    "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/pawbanner2.png",
                    ResourcePriority.MEDIUM,
                  ).src
                })`,
            backgroundSize: "contain",
            backgroundPosition: matches840px ? "center top" : "center top",
            backgroundRepeat: "repeat-y repeat-x",
            backgroundAttachment: "scroll",
          }}
        >
          {/* If video not loaded yet, use a simple color background */}
          {!preloadedVideosRef.current.donate && (
            <div className="absolute inset-0 bg-white"></div>
          )}

          {/* Impact Hero Section */}
          <div
            className="bg-transparent w-full pt-24 py-16 flex flex-col items-center text-center px-4"
            data-priority="medium"
          >
            <h1 className="!text-4xl md:!text-5xl !font-poppins text-orange !font-medium mb-6">
              Your Impact Starts Here
            </h1>
            <p className="!text-lg md:!text-3xl !font-poppins font-normal max-w-4xl mb-8">
              Sponsor a Pet is your trusted resource hub dedicated to supporting
              animal nonprofits everywhere.
            </p>
            <div className="text-left">
              <Link
                to="/signup"
                onClick={() => window.scrollTo(0, 0)}
                className="bg-blue hover:bg-blue-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
              >
                Sign up for free
              </Link>
            </div>
          </div>

          {/* Blue Buttons */}
          <div
            className="flex justify-center items-center w-full h-fit py-6 mb-8 px-4 md:px-8 overflow-visible"
            data-priority="medium"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-8 xl:gap-10 w-full max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-5xl xl:max-w-6xl justify-items-center">
              <BlueButton
                text="Donate"
                icon={MoneyWavy}
                onClick={() => changeVideo("donate")}
                isActive={activeVideoKey === "donate"}
                progress={buttonProgress.donate}
              />
              <BlueButton
                text="PetStar"
                icon={PawPrint}
                onClick={() => changeVideo("petstar")}
                isActive={activeVideoKey === "petstar"}
                progress={buttonProgress.petstar}
              />
              {/* Products button commented out - shop feature disabled */}
              {/* <BlueButton
                text="Products"
                icon={ShoppingCart}
                onClick={() => changeVideo("products")}
                isActive={activeVideoKey === "products"}
                progress={buttonProgress.products}
              /> */}
              <BlueButton
                text="Consulting"
                icon={Handshake}
                onClick={() => changeVideo("consulting")}
                isActive={activeVideoKey === "consulting"}
                progress={buttonProgress.consulting}
              />
            </div>
          </div>

          {/* Video section */}
          <div className="flex justify-center w-full px-2 mt-6 mb-16 md:mb-32 overflow-x-hidden">
            <div className="bg-white p-4 rounded-2xl shadow-2xl w-full max-w-[800px]">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                {isVideoLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-10 rounded-xl">
                    <div className="w-12 h-12 border-4 border-blue border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <video
                  ref={videoRef}
                  src={currentVideo}
                  title="Who We Are"
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                  autoPlay
                  playsInline
                  muted={isMuted}
                  onEnded={handleVideoEnd}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedData={() => setIsVideoLoading(false)}
                  preload="auto"
                  fetchpriority="high"
                  importance="high"
                  data-priority="critical"
                  style={{ contain: "paint" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* We Help NonProfits Fundraise Money */}
      <section className="flex flex-col items-center font-poppins px-4 sm:px-20 w-full mb-16 overflow-x-hidden">
        <div className="max-w-[1360px] w-full mx-auto">
          {/* Screen reader heading */}
          <h2 className="sr-only">WHAT WE DO</h2>

          {/* Visible label */}
          <p className="text-blue text-center !font-montserrat text-base font-bold mb-2">
            WHAT WE DO
          </p>

          <h3 className="text-3xl mt-2 !font-poppins text-center !font-semibold text-orange mb-12">
            We Help Non-Profits Fundraise Money
          </h3>
          <div className="flex md:justify-between justify-center gap-2 flex-wrap w-full ">
            <CircleFeature
              icon={Handshake}
              title="Non-Profits Asks For Help"
              description="Non-Profits turns to us for critical support and action"
              colorClass="stroke-[#F79E4C]"
              iconColorClass="text-[#F79E4C]"
            />
            <CircleFeature
              icon={Megaphone}
              title="We Advocate and Campaign"
              description="We passionately champion the rights and well-being of every pet"
              colorClass="stroke-[#3399CC]"
              iconColorClass="text-[#3399CC]"
              thickArcOnTop={true}
              descriptionClassName="max-w-[180px]"
            />
            <CircleFeature
              icon={MoneyWavy}
              title="We Send Money To Non-Profits"
              description="Channeling Support: Directing Funds to Non-profits for maximum impact"
              colorClass="stroke-[#F79E4C]"
              iconColorClass="text-[#F79E4C]"
            />
            <CircleFeature
              icon={HandHeart}
              title="Pets Receive Help"
              description="Transforming Lives: Ensuring every pet receives the care they deserve"
              colorClass="stroke-[#3399CC]"
              iconColorClass="text-[#3399CC]"
              thickArcOnTop={true}
            />
          </div>
        </div>
      </section>
      {/* Donate Campaigns */}
      <section className="flex flex-col md:flex-row justify-between items-center w-full lg:px-20 px-8 xl:px-32 py-6 lg:py-12 xl:py-20 mx-auto gap-4 overflow-x-hidden">
        <div className="max-w-[1360px] w-full mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="w-full md:w-1/2 text-left">
            {/* Screen reader heading */}
            <h2 className="sr-only">WAYS TO HELP ANIMALS</h2>

            {/* Visible label */}
            <p className="text-blue !font-montserrat font-bold mb-2 text-left">
              WAYS TO HELP ANIMALS
            </p>

            <h3 className="text-orange text-3xl !font-poppins !font-semibold mb-6 text-left">
              Donate Campaigns
            </h3>
            <p className="font-medium mb-6 text-left max-w-[560px]">
              Sponsor a Pet connects you with animal rescues around the world.
              Every dollar goes directly toward food, vet care, shelter, and
              safety for animals in need. Browse active campaigns and help save
              lives today.
            </p>
            <div className="text-left">
              <Link
                to="/campaigns/animal"
                onClick={() => window.scrollTo(0, 0)}
                className="bg-blue hover:bg-blue-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
              >
                View Campaigns
              </Link>
            </div>
          </div>
          <div className="w-full md:w-1/2 flex flex-col gap-4 items-center md:items-end">
            <div
              className="rounded-xl overflow-hidden shadow-lg w-full h-full"
              style={{
                maxWidth: "528.05px",
                height: "204px",
                maxHeight: "204px",
              }}
            >
              <img
                {...getImageProps(
                  "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/flowercat.png",
                  ResourcePriority.MEDIUM,
                )}
                data-priority="medium"
                alt="A cat smelling a flower"
                className="w-full h-full object-cover object-[center_30%]"
              />
            </div>
            <div
              className="rounded-xl overflow-hidden shadow-lg w-full h-full flex items-center justify-center"
              style={{
                maxWidth: "528.05px",
                height: "auto",
                maxHeight: "204px",
              }}
            >
              <img
                {...getImageProps(
                  "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/picnic.png",
                  ResourcePriority.MEDIUM,
                )}
                alt="People with dog at picnic"
                className="w-full h-full object-fit object-center"
                data-priority="medium"
              />
            </div>
          </div>
        </div>
      </section>
      {/* PetStar */}
      <section className="flex flex-col md:flex-row justify-between items-center w-full lg:px-20 px-8 xl:px-32 py-6 lg:py-12 xl:py-20 mx-auto gap-4 md:gap-16 overflow-x-hidden">
        <div className="max-w-[1360px] w-full mx-auto flex flex-col md:flex-row justify-between items-center gap-4 md:gap-16">
          <div className="w-full md:w-1/2 flex flex-row gap-4 justify-center md:justify-start order-2 md:order-1">
            <div
              className="rounded-xl overflow-hidden shadow-lg w-1/2 flex items-center justify-center"
              style={{
                maxWidth: "226.17px",
                height: "auto",
                maxHeight: "424px",
                aspectRatio: "226.17/424",
              }}
            >
              <img
                {...getImageProps(
                  "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/girlholdingcatup.png",
                )}
                alt="Woman with cat"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div
              className="rounded-xl overflow-hidden shadow-lg w-1/2 flex items-center justify-center"
              style={{
                maxWidth: "226.17px",
                height: "auto",
                maxHeight: "424px",
                aspectRatio: "226.17/424",
              }}
            >
              <img
                {...getImageProps(
                  "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/dogjumping.png",
                )}
                alt="Woman with dog in mountains"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div className="w-full md:w-1/2 text-left order-1 md:order-2">
            {/* Screen reader heading */}
            <h2 className="sr-only">THE PETSTAR CLUB</h2>

            {/* Visible label */}
            <p className="text-blue !font-montserrat font-bold mb-2 text-left">
              THE PETSTAR CLUB
            </p>

            <h3 className="text-orange text-3xl !font-poppins !font-semibold mb-6 text-left">
              Become A PetStar
            </h3>
            <p className="font-medium mb-6 text-left max-w-[555px]">
              PetStar is a way to help nonprofits while earning rewards! Sponsor
              a Pet is a fundraising platform dedicated to providing Animal
              Non-Profits a platform to share their stories and help raise
              necessary resources to save the lives of animals. We do this by
              working together with passionate local organizations to amplify
              our impact in communities as we come together as one.
            </p>
            <div className="text-left">
              <Link
                to="/petstar"
                onClick={() => window.scrollTo(0, 0)}
                className="bg-blue hover:bg-blue-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Browse, Shop and Make a Difference - commented out, shop feature disabled */}
      {/* <section className="flex flex-col md:flex-row justify-between items-center w-full lg:px-20 px-8 xl:px-32 py-6 lg:py-12 xl:py-20 mx-auto gap-4 overflow-x-hidden">
        <div className="max-w-[1360px] w-full mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="w-full md:w-1/2 text-left">
            <h2 className="sr-only">WAYS TO HELP ANIMALS</h2>
            <p className="text-blue !font-montserrat font-bold mb-2 text-left">
              WAYS TO HELP ANIMALS
            </p>
            <h3 className="text-orange text-3xl !font-poppins !font-semibold mb-6 text-left">
              Browse, Shop and Make a Difference
            </h3>
            <p className="font-medium mb-6 text-left max-w-[555px]">
              At Sponsor a Pet, every purchase you make directly supports our
              mission to rescue, rehabilitate, and re-home animals in need. Your
              shopping helps provide medical care, food, and shelter for pets
              waiting for a loving home, giving them a second chance at life.
              From unique products to special gifts, each item you buy directly
              contributes to the well-being of our furry, feathered, and scaly
              friends.Together, we're making a real difference—one product at a
              time.
            </p>
            <div className="text-left flex gap-6">
              <Link
                to="/shop"
                onClick={() => window.scrollTo(0, 0)}
                className="bg-blue hover:bg-blue-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
              >
                Visit Shop
              </Link>
              <Link
                to="/shop"
                onClick={() => window.scrollTo(0, 0)}
                className="bg-orange hover:bg-orange-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
              >
                SAP Merchandise
              </Link>
            </div>
          </div>
          <div className="w-full md:w-1/2 flex flex-row gap-4 justify-center md:justify-end">
            <div className="flex flex-col gap-4">
              <div
                className="w-full h-full rounded-xl overflow-hidden shadow-lg"
                style={{ maxWidth: "288.52px", height: "204px" }}
              >
                <img
                  {...getImageProps(
                    "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/treatbag.png",
                  )}
                  alt="Dog treats in bag"
                  className="w-full h-full object-cover"
                />
              </div>
              <div
                className="w-full h-full rounded-xl overflow-hidden shadow-lg"
                style={{ maxWidth: "288.52px", height: "204px" }}
              >
                <img
                  {...getImageProps(
                    "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/opensigncat.png",
                  )}
                  alt="Cat with open sign"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div
              className="h-full rounded-xl overflow-hidden shadow-lg"
              style={{ maxWidth: "211.66px", height: "426px" }}
            >
              <img
                {...getImageProps(
                  "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/treatjar.png",
                )}
                alt="Treat jar"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section> */}
      {/* Featured Campaigns */}
      <section className="w-full bg-blue py-10 px-4 overflow-x-hidden">
        <div className=" mx-auto text-center mb-6">
          {/* Screen reader heading */}
          <h2 className="sr-only">CHAMPION A CAUSE</h2>

          {/* Visible label */}
          <p className="text-white text-center !font-montserrat !text-base uppercase mb-2 font-bold">
            CHAMPION A CAUSE
          </p>

          <h3 className="text-4xl text-center !font-poppins !font-semibold text-blue-navy">
            Featured Campaigns
          </h3>
        </div>

        <style>
          {`
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .select-none * {
              -webkit-user-select: none !important;
              -moz-user-select: none !important;
              -ms-user-select: none !important;
              user-select: none !important;
            }
          `}
        </style>

        <div className="relative w-full mx-auto px-4 lg:px-0 select-none">
          {/* Left navigation button */}
          <div
            className="absolute transition-all duration-300 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer"
            style={{ left: width >= 1280 ? "60px" : "8px" }}
            onClick={() => {
              if (featuredCarouselPosition > 0) {
                if (featuredCarouselRef.current) {
                  const cardWidth = 278;
                  const gap = width >= 1280 ? 75 : 32;
                  const scrollAmount = cardWidth + gap;
                  const newPosition = Math.max(0, featuredCarouselPosition - 1);
                  setFeaturedCarouselPosition(newPosition);
                  featuredCarouselRef.current.scrollTo({
                    left: newPosition * scrollAmount,
                    behavior: "smooth",
                  });
                }
              }
              setIsFeaturedCarouselPaused(true);
              setTimeout(() => setIsFeaturedCarouselPaused(false), 10000);
            }}
            onMouseEnter={() => setIsFeaturedCarouselPaused(true)}
            onMouseLeave={() => setIsFeaturedCarouselPaused(false)}
          >
            {" "}
            <div
              className={`flex items-center transition-all duration-300 justify-center w-10 h-10 rounded-full border-2 border-white shadow-md ${
                featuredCarouselPosition === 0 ? "" : "bg-blue-navy"
              } `}
            >
              <CaretLeft
                size={24}
                className="transition-all duration-300 text-white"
                weight="bold"
              />{" "}
            </div>{" "}
          </div>{" "}
          <div
            ref={featuredCarouselRef}
            className="flex flex-row gap-10 xl:gap-[75px] max-w-[1360px] overflow-x-auto py-4 scroll-smooth hide-scrollbar"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              scrollSnapType: "x mandatory",
              maxWidth: "1360px",
              width: (() => {
                const cardWidth = 278;
                const gap = width >= 1280 ? 75 : 40;
                let visibleCards = 4;
                if (width < 670) visibleCards = 1;
                else if (width < 970) visibleCards = 2;
                else if (width < 1380) visibleCards = 3;

                // Ensure we properly limit the width when in mobile mode
                if (visibleCards < 4) {
                  return visibleCards * cardWidth + (visibleCards - 1) * gap;
                }
                return "1360px";
              })(),
              margin: "0 auto",
            }}
            onMouseEnter={() => setIsFeaturedCarouselPaused(true)}
            onMouseLeave={() => setIsFeaturedCarouselPaused(false)}
            onTouchStart={() => setIsFeaturedCarouselPaused(true)}
            onTouchEnd={() => setIsFeaturedCarouselPaused(false)}
          >
            {campaignsSubset.map((campaign) => (
              <div
                key={campaign.id}
                className="flex-shrink-0 scroll-snap-align-start"
                style={{ scrollSnapAlign: "start" }}
              >
                <FeaturedCampaignCard
                  campaign={campaign}
                  handleClick={handleClick}
                />
              </div>
            ))}
          </div>
          {/* Right navigation button */}
          <div
            className="absolute transition-all duration-300 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer"
            style={{ right: width >= 1280 ? "60px" : "8px" }}
            onClick={() => {
              let visibleCards = 4;
              if (width < 670) visibleCards = 1;
              else if (width < 970) visibleCards = 2;
              else if (width < 1380) visibleCards = 3;
              if (
                featuredCarouselPosition <
                campaignsSubset.length - visibleCards
              ) {
                if (featuredCarouselRef.current) {
                  const cardWidth = 278;
                  const gap = width >= 1280 ? 75 : 32;
                  const scrollAmount = cardWidth + gap;
                  const maxPosition = Math.max(
                    0,
                    campaignsSubset.length - visibleCards,
                  );
                  const newPosition = Math.min(
                    maxPosition,
                    featuredCarouselPosition + 1,
                  );
                  setFeaturedCarouselPosition(newPosition);
                  featuredCarouselRef.current.scrollTo({
                    left: newPosition * scrollAmount,
                    behavior: "smooth",
                  });
                }
              }
              setIsFeaturedCarouselPaused(true);
              setTimeout(() => setIsFeaturedCarouselPaused(false), 10000);
            }}
            onMouseEnter={() => setIsFeaturedCarouselPaused(true)}
            onMouseLeave={() => setIsFeaturedCarouselPaused(false)}
          >
            <div
              className={`flex items-center transition-all duration-300 justify-center w-10 h-10 rounded-full border-2 border-white shadow-md ${(() => {
                let visibleCards = 4;
                if (width < 700) visibleCards = 1;
                else if (width < 970) visibleCards = 2;
                else if (width < 1380) visibleCards = 3;

                return featuredCarouselPosition >=
                  campaignsSubset.length - visibleCards
                  ? ""
                  : "bg-blue-navy";
              })()} `}
            >
              <CaretRight
                size={24}
                className="transition-all duration-300 text-white"
                weight="bold"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 text-center max-w-[1360px] mx-auto">
          <Link
            to="/campaigns/animal"
            onClick={() => window.scrollTo(0, 0)}
            className="bg-orange hover:bg-orange-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
          >
            View More
          </Link>
        </div>
      </section>
      {/* Reasons to give to Sponsor a Pet */}
      <section className="flex gap-4 flex-col md:flex-row bg-blue-navy text-white lg:px-20 px-8 xl:px-32 py-6 lg:py-12 xl:py-20 mb-12 w-full mx-auto shadow-lg overflow-x-hidden">
        <div className="max-w-[1360px] w-full mx-auto flex gap-4 flex-col md:flex-row">
          <div className="md:w-3/5 md:flex md:items-center">
            <div className="w-full">
              <div className="mb-6 !font-montserrat items-start text-left">
                {/* Screen reader heading */}
                <h2 className="sr-only">MAKE A DIFFERENCE</h2>

                {/* Visible label */}
                <p className="uppercase text-sm !font-montserrat !font-semibold tracking-wide mb-1 text-left">
                  MAKE A DIFFERENCE
                </p>

                <h3 className="text-3xl md:text-4xl !font-montserrat !font-semibold text-orange mb-8 text-left">
                  Reasons to Give to Sponsor a Pet
                </h3>
              </div>

              <div className="space-y-8 text-left">
                <div className="flex items-center gap-4 md:gap-8 lg:gap-16 h-full">
                  {" "}
                  <div className="bg-white p-3 rounded-full flex-shrink-0 flex items-center justify-center">
                    {" "}
                    <MoneyWavy size={24} className="text-blue-navy" />{" "}
                  </div>{" "}
                  <div className="text-left">
                    {" "}
                    <h4 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      {" "}
                      Tax Deductible Donations{" "}
                    </h4>{" "}
                    <p className="!text-sm !md:text-base !font-montserrat text-left max-w-md">
                      {" "}
                      All Monetary Donations to our non-profit partners are tax
                      deductible.{" "}
                    </p>{" "}
                  </div>{" "}
                </div>{" "}
                <div className="flex items-center gap-4 md:gap-8 lg:gap-16 h-full">
                  {" "}
                  <div className="bg-white p-3 rounded-full flex-shrink-0 flex items-center justify-center">
                    {" "}
                    <ShieldCheck size={24} className="text-blue-navy" />{" "}
                  </div>{" "}
                  <div className="text-left">
                    {" "}
                    <h4 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      {" "}
                      Verified 501(c)(3) Organization{" "}
                    </h4>{" "}
                    <p className="!text-sm !md:text-base !font-montserrat text-left">
                      {" "}
                      Our verified non-profits partners pay nothing for our
                      services.{" "}
                    </p>{" "}
                  </div>{" "}
                </div>{" "}
                <div className="flex items-center gap-4 md:gap-8 lg:gap-16 h-full">
                  {" "}
                  <div className="bg-white p-3 rounded-full flex-shrink-0 flex items-center justify-center">
                    {" "}
                    <House size={24} className="text-blue-navy" />{" "}
                  </div>{" "}
                  <div className="text-left">
                    {" "}
                    <h4 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      {" "}
                      Join Our Community{" "}
                    </h4>{" "}
                    <p className="!text-sm !md:text-base !font-montserrat text-left max-w-lg">
                      {" "}
                      Working together amplifies our ability to accomplish our
                      goals of providing every animal love and a home.{" "}
                    </p>{" "}
                  </div>{" "}
                </div>
              </div>
            </div>
          </div>

          <div className="md:w-2/5 mt-6 md:mt-0 flex justify-center items-center gap-4">
            <div
              className="rounded-xl overflow-hidden shadow-lg w-1/2 flex items-center justify-center"
              style={{
                maxWidth: "226.17px",
                height: "auto",
                maxHeight: "424px",
                aspectRatio: "226.17/424",
              }}
            >
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/sleepycat.png"
                alt="Cat portrait"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div
              className="rounded-xl overflow-hidden shadow-lg w-1/2 flex items-center justify-center"
              style={{
                maxWidth: "226.17px",
                height: "auto",
                maxHeight: "424px",
                aspectRatio: "226.17/424",
              }}
            >
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/kissingdog.png"
                alt="Woman with dog"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>
      {/* Sponsor a Pet Offers */}
      <section className="flex flex-col w-full lg:px-20 px-8 xl:px-32 py-6 lg:py-12 xl:py-20 mx-auto overflow-x-hidden">
        <div className="max-w-[1360px] w-full mx-auto">
          <div className="text-center mb-8">
            {/* Screen reader heading */}
            <h2 className="sr-only">SPONSOR A PET OFFERS</h2>

            {/* Visible label */}
            <p className="text-blue !font-montserrat font-bold mb-2">
              SPONSOR A PET OFFERS
            </p>

            <h3 className="text-orange !text-3xl !font-montserrat !font-semibold">
              Rescue More, Stress Less: Your Hub for Nonprofit Support in Animal
              Rescue
            </h3>
          </div>

          <div className="flex flex-col md:flex-row justify-between gap-4 items-start">
            <div className="w-full md:w-2/5 flex justify-center md:justify-start mt-8 md:mt-0 order-2 md:order-1">
              <div className="flex flex-col gap-4">
                <div
                  className="rounded-xl overflow-hidden shadow-lg w-full flex items-center justify-center"
                  style={{
                    maxWidth: "407px",
                    height: "auto",
                    maxHeight: "170.33px",
                  }}
                >
                  <img
                    src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/onthreev2.png"
                    alt="Golden retriever dog"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div
                  className="rounded-xl overflow-hidden shadow-lg w-full flex items-center justify-center"
                  style={{
                    maxWidth: "407px",
                    height: "auto",
                    maxHeight: "170.33px",
                  }}
                >
                  <img
                    src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/fundraisinglong.png"
                    alt="Woman with cat"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div
                  className="rounded-xl overflow-hidden shadow-lg w-full flex items-center justify-center"
                  style={{
                    maxWidth: "407px",
                    height: "auto",
                    maxHeight: "170.33px",
                  }}
                >
                  <img
                    src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/notepad.png"
                    alt="Cat with flowers"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div
                  className="rounded-xl overflow-hidden shadow-lg w-full flex items-center justify-center"
                  style={{
                    maxWidth: "407px",
                    height: "auto",
                    maxHeight: "170.33px",
                  }}
                >
                  <img
                    src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/operations.png"
                    alt="People with dog at picnic"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>

            <div className="w-full md:w-3/5 text-left order-1 md:order-2 md:pl-6">
              <h4 className="text-orange mt-12 !font-semibold text-3xl !font-poppins mb-12 text-left">
                Free Resources
              </h4>

              <div className="space-y-8 text-left">
                <div className="flex items-start gap-4 md:gap-8 lg:gap-16">
                  <div className="bg-blue text-white p-4 rounded-full flex-shrink-0">
                    <MoneyWavy size={24} className="text-white" />
                  </div>
                  <div className="text-left -mt-4">
                    <h5 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      FUNDRAISING CAMPAIGN TOOLS
                    </h5>
                    <p className="!text-sm font-medium md:!text-base max-w-[600px] !font-montserrat text-left ">
                      Level up your fundraising efforts with our free checklist,
                      calendar, and sponsor thank-you letter templates.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 md:gap-8 lg:gap-16">
                  <div className="bg-blue text-white p-4 rounded-full flex-shrink-0">
                    <Download size={24} className="text-white" />
                  </div>
                  <div className="text-left -mt-4">
                    <h5 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      FREE AUCTION HOSTING RESOURCES
                    </h5>
                    <p className="!text-sm font-medium md:!text-base max-w-[600px] !font-montserrat text-left ">
                      Download our free templates to host a successful
                      auction—bid sheets, rules and guidelines, and donation
                      request letters. Simplify your event planning and maximize
                      fundraising impact!
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 md:gap-8 lg:gap-16">
                  <div className="bg-blue text-white p-4 rounded-full flex-shrink-0">
                    <PresentationChart size={24} className="text-white" />
                  </div>
                  <div className="text-left -mt-4">
                    <h5 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      EVENT PLANNING RESOURCES
                    </h5>
                    <p className="!text-sm font-medium md:!text-base max-w-[600px] !font-montserrat text-left ">
                      Streamline your next fundraising event with our free
                      resources—checklists, gala invites, and workflow guides to
                      keep every detail on track.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 md:gap-8 lg:gap-16">
                  <div className="bg-blue text-white p-4 rounded-full flex-shrink-0">
                    <HandHeart size={24} className="text-white" />
                  </div>
                  <div className="text-left -mt-4">
                    <h5 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      DONOR ENGAGEMENT TEMPLATES
                    </h5>
                    <p className="!text-sm font-medium md:!text-base max-w-[600px] !font-montserrat text-left ">
                      Strengthen your donor relationships with our free
                      resources—phone call scripts, Giving Tuesday appeal
                      letters, and welcome email series templates.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 md:gap-8 lg:gap-16">
                  <div className="bg-blue text-white p-4 rounded-full flex-shrink-0">
                    <ClipboardText size={24} className="text-white" />
                  </div>
                  <div className="text-left -mt-4">
                    <h5 className="font-semibold !font-montserrat text-lg mb-1 text-left">
                      NONPROFIT GROWTH TOOLS
                    </h5>
                    <p className="!text-sm font-medium md:!text-base max-w-[600px] !font-montserrat text-left ">
                      Streamline your nonprofit's operations with our free
                      templates—development plans, business plans, and pitch
                      worksheets.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link
                  to="/resources"
                  onClick={() => window.scrollTo(0, 0)}
                  className="bg-orange hover:bg-orange-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
                >
                  All Resources
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Our Services */}{" "}
      <section className="flex flex-col items-center w-full py-16 lg:py-24 overflow-hidden">
        {" "}
        <div className="max-w-[1360px] w-full mx-auto">
          {" "}
          {/* Screen reader heading */}
          <h2 className="sr-only">OUR SERVICES</h2>
          {/* Visible label */}
          <p className="text-blue text-center !font-montserrat text-base font-bold mb-2">
            {" "}
            OUR SERVICES{" "}
          </p>{" "}
          <h3 className="text-orange !text-3xl !font-montserrat text-center font-bold mb-12 lg:mb-16">
            {" "}
            Amplifying Results With Modern Solutions{" "}
          </h3>{" "}
          <div className="w-full mx-auto relative select-none">
            {/* Left navigation button */}
            <div
              className="absolute top-1/2 transform -translate-y-1/2 z-10 cursor-pointer "
              style={{
                position: "absolute",
                left:
                  width >= 1750 ? "-180px" : width >= 1560 ? "-90px" : "8px",
                opacity: servicesCarouselPosition === 0 ? 0.5 : 1,
                pointerEvents: servicesCarouselPosition === 0 ? "none" : "auto",
                transition: "opacity 0.3s ease",
              }}
              onClick={() => {
                if (servicesCarouselRef.current) {
                  const cardWidth = 290;
                  const gap = width >= 1280 ? 60 : 32;
                  const scrollAmount = cardWidth + gap;
                  const newPosition = Math.max(0, servicesCarouselPosition - 1);
                  setServicesCarouselPosition(newPosition);
                  servicesCarouselRef.current.scrollTo({
                    left: newPosition * scrollAmount,
                    behavior: "smooth",
                  });
                }
                setIsServicesCarouselPaused(true);
                setTimeout(() => setIsServicesCarouselPaused(false), 10000);
              }}
              onMouseEnter={() => setIsServicesCarouselPaused(true)}
              onMouseLeave={() => setIsServicesCarouselPaused(false)}
            >
              <div
                className={`flex items-center transition-all duration-300 justify-center w-10 h-10 rounded-full border-2 border-blue-navy shadow-md ${
                  servicesCarouselPosition === 0 ? "bg-white" : "bg-blue-navy"
                }`}
              >
                <CaretLeft
                  size={24}
                  className={`transition-all duration-300 ${
                    servicesCarouselPosition === 0
                      ? "text-blue-navy"
                      : "text-white"
                  }`}
                  weight="bold"
                />
              </div>
            </div>

            {/* Carousel */}
            <div className="w-full px-4 lg:px-0 overflow-hidden">
              <div
                ref={servicesCarouselRef}
                className="flex flex-row gap-8 xl:gap-[60px] overflow-x-auto py-6 scroll-smooth hide-scrollbar"
                style={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  scrollSnapType: "x mandatory",
                  maxWidth: "1360px",
                  width: (() => {
                    const cardWidth = 290;
                    const gap = width >= 1280 ? 60 : 32;
                    let visibleCards = 4;
                    if (width < 670) visibleCards = 1;
                    else if (width < 970) visibleCards = 2;
                    else if (width < 1380) visibleCards = 3;

                    // Ensure we properly limit the width when in mobile mode
                    if (visibleCards < 4) {
                      return (
                        visibleCards * cardWidth + (visibleCards - 1) * gap
                      );
                    }
                    return "1360px";
                  })(),
                  margin: "0 auto",
                }}
                onMouseEnter={() => setIsServicesCarouselPaused(true)}
                onMouseLeave={() => setIsServicesCarouselPaused(false)}
                onTouchStart={() => setIsServicesCarouselPaused(true)}
                onTouchEnd={() => setIsServicesCarouselPaused(false)}
              >
                {services.map((service) => (
                  <div
                    key={service.id}
                    className="flex-shrink-0 scroll-snap-align-start"
                    style={{ scrollSnapAlign: "start", width: "290px" }}
                  >
                    <ServiceCard
                      key={service.id}
                      image={service.image}
                      title={service.title}
                      description={service.description}
                      link={service.link}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right navigation button */}
            <div
              className="absolute top-1/2 transform -translate-y-1/2 z-10 cursor-pointer"
              style={{
                position: "absolute",
                right:
                  width >= 1750 ? "-180px" : width >= 1560 ? "-90px" : "8px",
                opacity: (() => {
                  let visibleCards = 4;
                  if (width < 670) visibleCards = 1;
                  else if (width < 970) visibleCards = 2;
                  else if (width < 1380) visibleCards = 3;
                  return servicesCarouselPosition >= 9 - visibleCards ? 0.5 : 1;
                })(),
                pointerEvents: (() => {
                  let visibleCards = 4;
                  if (width < 670) visibleCards = 1;
                  else if (width < 970) visibleCards = 2;
                  else if (width < 1380) visibleCards = 3;
                  return servicesCarouselPosition >= 9 - visibleCards
                    ? "none"
                    : "auto";
                })(),
                transition: "opacity 0.3s ease",
              }}
              onClick={() => {
                let visibleCards = 4;
                if (width < 670) visibleCards = 1;
                else if (width < 970) visibleCards = 2;
                else if (width < 1380) visibleCards = 3;
                if (servicesCarouselPosition < 9 - visibleCards) {
                  if (servicesCarouselRef.current) {
                    const cardWidth = 290;
                    const gap = width >= 1280 ? 60 : 32;
                    const scrollAmount = cardWidth + gap;
                    const maxPosition = Math.max(0, 9 - visibleCards);
                    const newPosition = Math.min(
                      maxPosition,
                      servicesCarouselPosition + 1,
                    );
                    setServicesCarouselPosition(newPosition);
                    servicesCarouselRef.current.scrollTo({
                      left: newPosition * scrollAmount,
                      behavior: "smooth",
                    });
                  }
                }
                setIsServicesCarouselPaused(true);
                setTimeout(() => setIsServicesCarouselPaused(false), 10000);
              }}
              onMouseEnter={() => setIsServicesCarouselPaused(true)}
              onMouseLeave={() => setIsServicesCarouselPaused(false)}
            >
              <div
                className={`flex items-center transition-all duration-300 justify-center w-10 h-10 rounded-full border-2 border-blue-navy shadow-md ${(() => {
                  let visibleCards = 4;
                  if (width < 670) visibleCards = 1;
                  else if (width < 970) visibleCards = 2;
                  else if (width < 1380) visibleCards = 3;
                  return servicesCarouselPosition >= 9 - visibleCards
                    ? "bg-white"
                    : "bg-blue-navy";
                })()} `}
              >
                <CaretRight
                  size={24}
                  className={`transition-all duration-300 ${(() => {
                    let visibleCards = 4;
                    if (width < 670) visibleCards = 1;
                    else if (width < 970) visibleCards = 2;
                    else if (width < 1380) visibleCards = 3;
                    return servicesCarouselPosition >= 9 - visibleCards
                      ? "text-blue-navy"
                      : "text-white";
                  })()}`}
                  weight="bold"
                />
              </div>
            </div>
          </div>
          <div className="mt-10 lg:mt-16 text-center">
            <Link
              to="/consulting-services"
              onClick={() => window.scrollTo(0, 0)}
              className="bg-orange hover:bg-orange-dark1 text-white text-lg shadow-xl transition-all duration-300 font-semibold py-4 px-8 rounded-full inline-block"
            >
              All Services
            </Link>
          </div>
        </div>
      </section>
      {/* Join the Community */}
      <section className="w-full relative overflow-hidden">
        <div
          className="w-full py-10 relative flex items-center overflow-hidden"
          style={{
            backgroundImage: `url(${
              getImageProps(
                "https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/homepage-images/dogandcat.png",
                ResourcePriority.MEDIUM,
              ).src
            })`,
            backgroundSize: "cover",
            backgroundPosition: matches840px ? "30% 30%" : "center 30%",
            height: "min(40vh, 300px)",
            minHeight: "250px",
          }}
          data-priority="medium"
        >
          <div className="absolute inset-0 bg-blue bg-opacity-30"></div>
          <div className="flex flex-col w-full lg:px-20 px-8 xl:px-32 py-6 relative z-10">
            <div className="max-w-[1360px] w-full mx-auto">
              <div className={`${matches840px ? "-mt-16" : ""}`}>
                {/* Screen reader heading */}
                <h2 className="sr-only">JOIN THE COMMUNITY</h2>

                {/* Visible label */}
                <p className="text-blue-navy uppercase !font-montserrat text-base !font-bold mb-2 text-left">
                  JOIN THE COMMUNITY
                </p>
                <h3
                  className={`text-blue-navy !font-montserrat !font-bold mb-2 text-left ${
                    matches840px ? "text-2xl" : "text-4xl"
                  }`}
                >
                  <span className="block font-bold">
                    Sign Up for Free & Start
                  </span>
                  <span className="block font-bold">Fundraising Today</span>
                </h3>

                <div className="text-left">
                  <Link
                    to="/signup"
                    onClick={() => window.scrollTo(0, 0)}
                    className="bg-orange hover:bg-orange-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
                  >
                    Sign Up
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Map and partners */}
      <section className="flex !flex-col !justify-center items-center !pb-12 md:!px-8 bg-white w-full h-full overflow-x-hidden">
        <div className="text-center max-w-4xl mx-auto mb-12 mt-14 px-4">
          {/* Screen reader heading */}
          <h2 className="sr-only">OUR PARTNERS</h2>

          {/* Visible label */}
          <p className="text-blue !font-bold !font-montserrat !text-base mb-2">
            OUR PARTNERS
          </p>
          <h3 className="text-orange !text-3xl !pointer-events-autofont-semibold !font-poppins mb-6 font-bold">
            Find a Non-Profit Near You
          </h3>
          <p className="!text-base !font-montserrat !font-medium px-4">
            Our rescues grow by the day, there's room for yours too! Hover over
            a state to view a list of all nonprofits located there.
          </p>
          <div className="mt-8 mb-14">
            <Link
              to="/non-profit"
              onClick={() => window.scrollTo(0, 0)}
              className="bg-blue hover:bg-blue-dark1 text-white text-sm shadow-xl transition-all duration-300 font-semibold py-2 px-6 rounded-full inline-block"
            >
              All Partners
            </Link>
          </div>
        </div>
        <UsaMap />

        <div className={`${classes.hompage_section} p-0 !w-full my-8`}>
          {width >= 1200 ? (
            <Slider {...logoCarouselSettings}>
              {
                // Rendering logos
                logoList.map((logoUrl) => (
                  <div className={classes.logo_slide} key={logoUrl}>
                    <img src={logoUrl} alt="" />
                  </div>
                ))
              }
            </Slider>
          ) : width >= 768 ? (
            <Slider {...logoCarouselSettingsMedium}>
              {
                // Rendering logos
                logoList.map((logoUrl) => (
                  <div className={classes.logo_slide} key={logoUrl}>
                    <img src={logoUrl} alt="" />
                  </div>
                ))
              }
            </Slider>
          ) : width >= 0 ? (
            <Slider {...logoCarouselSettingsSmall}>
              {
                // Rendering logos
                logoList.map((logoUrl) => (
                  <div className={classes.logo_slide} key={logoUrl}>
                    <img src={logoUrl} alt="" />
                  </div>
                ))
              }
            </Slider>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default HomePage;
